import { useEffect, useState } from 'react'
import type { HoursParsed, HoursSegment } from '~/lib/api/types'
import { formatTime } from '~/lib/time/format'
import styles from './hours-bar.module.scss'

type Props = {
  hoursParsed: HoursParsed
  parseStatus?: string
  hoursRaw?: string | null
  slackPermalink?: string | null
}

const SCALE_START_HOUR = 6 // 6 AM
const SCALE_END_HOUR = 22 // 10 PM
const TICKS = [9, 12, 15, 18] // every 3 hours, exclusive of endpoints

export function HoursBar({
  hoursParsed,
  parseStatus,
  hoursRaw,
  slackPermalink,
}: Props) {
  const nowPct = useNowPercent()

  if (parseStatus === 'failed') {
    return (
      <div className={styles.failed}>
        <span className={styles.hairline} aria-hidden />
        <span className={`${styles.failedText} mono`} title="parse failed">
          {hoursRaw || '—'}
        </span>
        <a
          href={slackPermalink || 'slack://open'}
          className={styles.fixLink}
          rel="noreferrer"
        >
          fix in Slack →
        </a>
      </div>
    )
  }

  const segments = effectiveSegments(hoursParsed)

  return (
    <div className={styles.bar} role="img" aria-label={describe(segments)}>
      <div className={styles.scale}>
        {TICKS.map(h => {
          const pct =
            ((h - SCALE_START_HOUR) / (SCALE_END_HOUR - SCALE_START_HOUR)) * 100
          return (
            <span
              key={h}
              className={styles.tick}
              style={{ left: `${pct}%` }}
              aria-hidden
            />
          )
        })}
        {segments.map((seg, i) => {
          const left = pct(seg.start)
          const width = Math.max(pct(seg.end) - left, 1.5)
          return (
            <span
              key={`${seg.start}-${i}`}
              className={styles.segment}
              style={{ left: `${left}%`, width: `${width}%` }}
              title={`${formatTime(seg.start)} – ${formatTime(seg.end)}`}
            />
          )
        })}
        {nowPct !== null && (
          <span
            className={styles.now}
            style={{ left: `${nowPct}%` }}
            aria-label="now"
          />
        )}
      </div>
      <div className={styles.scaleLabels}>
        <span className={styles.scaleLabel}>6a</span>
        <span className={styles.scaleLabel}>10p</span>
      </div>
    </div>
  )
}

// Single contiguous spans come back as `{start, end, segments: []}`. The bar
// only renders from `segments`, so fall back to the outer span when the array
// is empty — otherwise the day looks blank.
function effectiveSegments(
  parsed: HoursParsed | null | undefined
): HoursSegment[] {
  const segs = parsed?.segments ?? []
  if (segs.length > 0) return segs
  if (parsed?.start && parsed?.end && !parsed.ooo) {
    return [{ start: parsed.start, end: parsed.end }]
  }
  return []
}

function pct(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  const hours = (h ?? 0) + (m ?? 0) / 60
  const clamped = Math.max(SCALE_START_HOUR, Math.min(SCALE_END_HOUR, hours))
  return (
    ((clamped - SCALE_START_HOUR) / (SCALE_END_HOUR - SCALE_START_HOUR)) * 100
  )
}

function describe(segments: { start: string; end: string }[]): string {
  if (segments.length === 0) return 'No hours yet'
  return segments
    .map(s => `${formatTime(s.start)} to ${formatTime(s.end)}`)
    .join(', ')
}

function useNowPercent(): number | null {
  const [pctNow, setPctNow] = useState<number | null>(() => computeNowPct())

  useEffect(() => {
    const id = window.setInterval(() => setPctNow(computeNowPct()), 60_000)
    return () => window.clearInterval(id)
  }, [])

  return pctNow
}

function computeNowPct(): number | null {
  const now = new Date()
  const hours = now.getHours() + now.getMinutes() / 60
  if (hours < SCALE_START_HOUR || hours > SCALE_END_HOUR) return null
  return (
    ((hours - SCALE_START_HOUR) / (SCALE_END_HOUR - SCALE_START_HOUR)) * 100
  )
}
